import { Icon } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { Permission } from '@/types/permissions';
import { ActionBarInteractionTrigger } from './ActionBarShell';

export enum ActionType {
    ITEM = 'item',
    TOGGLE = 'toggle',
    MENU = 'menu',
    SEARCH = 'search',
    DIVIDER = 'divider',
    CUSTOM = 'custom',
}

export enum ActiveIndicator {
    CHECK = 'check',
    DOT = 'dot',
}

export enum ActionVariant {
    DEFAULT = 'default',
    DANGER = 'danger',
    PRIMARY = 'primary',
}

export enum DividerOrientation {
    HORIZONTAL = 'horizontal',
    VERTICAL = 'vertical',
}

export interface ItemAction {
    type: ActionType.ITEM;
    icon?: Icon;
    label: string;
    onClick?: () => void;
    onClear?: () => void;
    content?: ReactNode;
    permission?: Permission;
    disabled?: boolean;
    active?: boolean;
    maxWidth?: string;
    variant?: ActionVariant;
    trailingBadge?: ReactNode;
}

export interface ToggleAction {
    type: ActionType.TOGGLE;
    icon: Icon;
    activeIcon?: Icon;
    indeterminateIcon?: Icon;
    label: string;
    isActive: boolean;
    indeterminate?: boolean;
    onClick: () => void;
    slideContent?: ReactNode;
    permission?: Permission;
}

export interface MenuItemRow {
    icon?: Icon;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    isActive?: boolean;
    activeIndicator?: ActiveIndicator;
    alignOffset?: number;
    children?: ReactNode;
    trailingBadge?: ReactNode;
    testId?: string;
}

export interface MenuItemSeparator {
    separator: true;
}

export type MenuItemConfig = MenuItemRow | MenuItemSeparator;

export interface MenuAction {
    type: ActionType.MENU;
    icon: Icon;
    label: string;
    items: MenuItemConfig[];
    trigger?: ActionBarInteractionTrigger;
    title?: string;
    active?: boolean;
    activeIndicator?: ActiveIndicator;
    onClear?: () => void;
    permission?: Permission;
    variant?: ActionVariant.DEFAULT | ActionVariant.PRIMARY;
    dropdownAlign?: 'start' | 'center' | 'end';
    disabled?: boolean;
    triggerTestId?: string;
}

export interface SearchAction {
    type: ActionType.SEARCH;
    value: string;
    onChange: (value: string) => void;
    onSubmit?: (value: string) => void;
    placeholder?: string;
    isExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    permission?: Permission;
}

export interface DividerAction {
    type: ActionType.DIVIDER;
    orientation?: DividerOrientation;
    permission?: Permission;
}

export interface CustomAction {
    type: ActionType.CUSTOM;
    content: ReactNode;
    permission?: Permission;
}

export type ActionConfig =
    | ItemAction
    | ToggleAction
    | MenuAction
    | SearchAction
    | DividerAction
    | CustomAction;
